import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools, persist } from "zustand/middleware";
import localforage from "localforage";
import { v4 as uuidv4 } from "uuid";
import {
  CountEntryOfItemData,
  CountEntryRequestData,
} from "../api/CycleCountBatchs";
// Type-only reuse so this queue stays shape-compatible with the work-order
// offline queue (lets the sync dispatcher merge both and the upload UI render
// both). This does NOT reuse the WO store instance — cycle count has its own.
import type { OperationDetails, StatusType } from "./wo-offline-updates";

export const SUBMIT_CYCLE_COUNT = "Submitting Cycle Count";

export type { OperationDetails, StatusType };

// A queued offline cycle-count submission: the count-entry payload plus the
// batch it belongs to (used for post-sync refresh) and the operation metadata.
type CycleCountSubmissionData = {
  batchId: number;
} & CountEntryOfItemData;

export type CycleCountSubmission = OperationDetails & CycleCountSubmissionData;

type CycleCountOfflineUpdatesState = {
  cycleCountsToSubmit: CycleCountSubmission[];
  operationDetails: OperationDetails[];
};

type CycleCountOfflineUpdatesAction = {
  // Coalesce a counted item into the batch's single queued submission (creating
  // it on first item). Counting many items offline => one submission => one API
  // call on reconnect.
  upsertCycleCountItem: (
    batchId: number,
    item: CountEntryRequestData
  ) => void;
  getCycleCountSubmissionById: (id: string) => CycleCountSubmission | undefined;
  // replace a submission's items (e.g. keep only the items that failed to sync
  // so a retry re-sends just those, not the ones the server already accepted)
  updateSubmissionRequestObject: (
    id: string,
    requestObject: CountEntryRequestData[]
  ) => void;
  makeOperationCompletedById: (id: string) => void;
  updateStatusById: (id: string, status: StatusType, error?: string[]) => void;
  setSyncCounts: (
    id: string,
    successCount: number,
    failedCount: number
  ) => void;
  deleteById: (id: string) => void;
  deleteAllSuccessFullOps: () => void;
  hasFailedOperationBefore: (id: string) => boolean;
  reset: () => void;
};

function getPendingOperationDetails(
  operation: string,
  batchId: number
): OperationDetails {
  return {
    id: uuidv4(),
    operation,
    status: "pending",
    createdDate: new Date(),
    batchId,
  };
}

const defaultState: CycleCountOfflineUpdatesState = {
  cycleCountsToSubmit: [],
  operationDetails: [],
};

export const useCycleCountOfflineUpdatesStore = create<
  CycleCountOfflineUpdatesState & CycleCountOfflineUpdatesAction
>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...defaultState,
        upsertCycleCountItem(batchId, item) {
          set((state) => {
            const existing = state.cycleCountsToSubmit.find(
              (c) => c.batchId === batchId
            );
            if (existing) {
              const idx = existing.RequestObject.findIndex(
                (i) => i.CountId === item.CountId
              );
              if (idx !== -1) existing.RequestObject[idx] = item;
              else existing.RequestObject.push(item);
              // re-arm the queued op if it had previously failed
              const op = state.operationDetails.find(
                (o) => o.id === existing.id
              );
              if (op) {
                op.status = "pending";
                op.error = undefined;
              }
            } else {
              const operation = getPendingOperationDetails(
                SUBMIT_CYCLE_COUNT,
                batchId
              );
              state.cycleCountsToSubmit.push({
                batchId,
                RequestObject: [item],
                Confirmation: {},
                ...operation,
              });
              state.operationDetails.push(operation);
            }
          });
        },

        getCycleCountSubmissionById(id) {
          return get().cycleCountsToSubmit.find((c) => c.id === id);
        },

        updateSubmissionRequestObject(id, requestObject) {
          set((state) => {
            const index = state.cycleCountsToSubmit.findIndex(
              (c) => c.id === id
            );
            if (index !== -1) {
              state.cycleCountsToSubmit[index].RequestObject = requestObject;
            }
          });
        },

        makeOperationCompletedById(id) {
          set((state) => {
            state.operationDetails = state.operationDetails.map((o) => {
              if (o.id === id)
                return {
                  ...o,
                  id: uuidv4(),
                  status: "success",
                  error: undefined,
                };
              return o;
            });
          });
          //remove the payload from storage; keep the (now success) op detail
          get().deleteById(id);
        },

        updateStatusById(id, status, error) {
          set((state) => {
            for (const key in state) {
              const value =
                state[key as keyof CycleCountOfflineUpdatesState];
              if (Array.isArray(value)) {
                const index = value.findIndex((item: any) => item.id === id);
                if (index !== -1) {
                  Object.assign(value[index], { status, error });
                }
              }
            }
          });
        },

        setSyncCounts(id, successCount, failedCount) {
          set((state) => {
            const op = state.operationDetails.find((o) => o.id === id);
            if (op) {
              op.syncSuccessCount = successCount;
              op.syncFailedCount = failedCount;
            }
          });
        },

        deleteById(id) {
          set((state) => {
            state.cycleCountsToSubmit = state.cycleCountsToSubmit.filter(
              (c) => c.id !== id
            );
            // also drop the queue entry this screen renders from; without this
            // the row stays visible after delete
            state.operationDetails = state.operationDetails.filter(
              (o) => o.id !== id
            );
          });
        },

        deleteAllSuccessFullOps() {
          set((state) => {
            state.operationDetails = state.operationDetails.filter(
              (o) => o.status !== "success"
            );
          });
        },

        hasFailedOperationBefore(id) {
          const { operationDetails } = get();
          const ind = operationDetails.findIndex((o) => o.id === id);
          if (ind === -1) return false;
          return operationDetails
            .slice(0, ind)
            .some((o) => o.status === "failed");
        },

        reset() {
          set(() => defaultState);
        },
      })),
      {
        name: "cycle-count-offline-updates",
        storage: {
          getItem: (key: string): Promise<any> => localforage.getItem(key),
          setItem: (key: string, value: any): Promise<void> =>
            localforage.setItem(key, value),
          removeItem: (key: string): Promise<void> =>
            localforage.removeItem(key),
        },
        partialize: (state) => ({
          cycleCountsToSubmit: state.cycleCountsToSubmit,
          operationDetails: state.operationDetails,
        }),
      }
    ),
    {
      name: "CycleCountOfflineUpdatesStore",
      enabled: true,
    }
  )
);
